import { Locales } from '@griply/types';
import {
  getAdditionalUserInfo,
  OAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import i18n from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { getLoggableError, getRollbar } from 'shared/services/error-tracking';
import {
  clearOnboardingAnswers,
  getNonce,
} from 'shared/services/local-storage';
import { getSearchParamIdToken } from 'shared/utils/get-search-param-id-token';

import { finishUserSignup } from './finish-user-signup';
import { getUser } from './get-user';
import { getAuthentication } from './helpers';
import { channelLogin } from './helpers/auth';
import { processOnboardingAnswers } from './process-onboarding-answers';
import { setPushTokenOnUser } from './set-push-token-on-user';
import { setUserCountry } from './set-user-country';

type AppleJwt = {
  iss?: string;
  user?: { firstName: string; lastName: string };
  state?: string;
};

const signInWithApple = async (token: string) => {
  // get the nonce used for sign-in
  const nonce = getNonce();
  const rawNonce = nonce?.raw;

  try {
    const decryptedToken = jwtDecode<AppleJwt>(token);
    const { user, state } = decryptedToken;

    const auth = await getAuthentication();

    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: token,
      rawNonce,
    });

    const result = await signInWithCredential(auth, credential);
    const isSignup = getAdditionalUserInfo(result)?.isNewUser ?? false;

    // if signup, finish signup
    if (isSignup) {
      await finishUserSignup({
        uid: auth.currentUser?.uid,
        name: user ? `${user?.firstName} ${user.lastName}` : undefined,
        locale: (i18n.resolvedLanguage ?? i18n.language) as Locales,
        queryParameters: state ? JSON.parse(state) : undefined,
      });

      // Process onboarding answers after signup
      await processOnboardingAnswers();
      clearOnboardingAnswers();
    }
  } catch (error) {
    getRollbar().error(
      'Error signing in with Apple - signInWithCredential or finishUserSignup failed',
      error as Error,
      {
        ...getLoggableError(error),
        nonce: nonce?.hashed,
        hasRawNonce: !!rawNonce,
        hasToken: !!token,
      },
    );
  }
};

export const signInWithToken = async () => {
  const token = getSearchParamIdToken();

  if (!token) {
    return;
  }

  const auth = await getAuthentication();
  // make sure there is no user active
  await auth.signOut();

  try {
    await signInWithApple(token);
  } catch (error) {
    getRollbar().error(
      'Error signing in with Apple - signInWithApple threw',
      error as Error,
      getLoggableError(error),
    );
  }

  try {
    const user = await getUser();

    if (!user) {
      return;
    }

    await setPushTokenOnUser(user.uid, user.settings?.language || Locales.EnUS);
  } catch (error) {
    getRollbar().error(
      'Error signing in with Apple - setPushTokenOnUser failed',
      error as Error,
      getLoggableError(error),
    );
  }

  try {
    await setUserCountry();
  } catch (error) {
    getRollbar().error(
      'Error signing in with Apple - setUserCountry failed',
      error as Error,
      getLoggableError(error),
    );
  }

  // log other tabs in as well
  channelLogin();
};
